import { useCallback, useState, type FormEvent } from 'react'
import { LOADING_LABELS } from '../constants/loading'
import type { LocationSelection } from '../constants/location'
import {
  EMPTY_LOCATION_FORM,
  LOCATIONS_PAGE,
  VENDOR_BADGES,
  type LocationFormState,
  type VendorLocation,
} from '../constants/vendor'
import { ApiError } from '../helpers/api-client'
import { displayAddress, formatCoordinates } from '../helpers/format'
import {
  locationToForm,
  updateVendorLocation,
} from '../helpers/vendor-api'
import { statusToggleLabel } from '../helpers/vendor'
import { useAuth } from '../hooks/useAuth'
import { useVendorData } from '../hooks/useVendorData'
import { LocationSearchInput } from '../components/LocationSearchInput'
import { Modal } from '../components/Modal'
import { PageLoader } from '../components/PageLoader'
import { Spinner } from '../components/Spinner'
import { StatusToggle } from '../components/StatusToggle'
import { Topbar } from '../components/Topbar'
import '../styles/catalog-page.scss'
import '../styles/catalog-form.scss'
import '../styles/vendor-table.scss'

export function LocationsPage() {
  const { vendorId } = useAuth()
  const { location, loading, error, refresh } = useVendorData()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<VendorLocation | null>(null)
  const [form, setForm] = useState<LocationFormState>(EMPTY_LOCATION_FORM)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  function openCreate() {
    setEditing(null)
    setForm(EMPTY_LOCATION_FORM)
    setFormError('')
    setModalOpen(true)
  }

  function openEdit(shop: VendorLocation) {
    setEditing(shop)
    setForm(locationToForm(shop))
    setFormError('')
    setModalOpen(true)
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!vendorId) return
    if (!form.name.trim() || !form.latitude || !form.longitude) {
      setFormError(LOCATIONS_PAGE.required)
      return
    }
    setSaving(true)
    setFormError('')
    try {
      await updateVendorLocation(vendorId, form)
      setModalOpen(false)
      await refresh()
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : LOCATIONS_PAGE.saveError,
      )
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(shop: VendorLocation) {
    if (!vendorId) return
    setBusyId(shop.id)
    try {
      await updateVendorLocation(vendorId, {
        ...locationToForm(shop),
        isActive: !shop.isActive,
      })
      await refresh()
    } finally {
      setBusyId(null)
    }
  }

  const handleLocationSelect = useCallback((place: LocationSelection) => {
    setForm((current) => ({
      ...current,
      name: current.name.trim() || place.name,
      address: place.address,
      latitude: String(place.latitude),
      longitude: String(place.longitude),
    }))
  }, [])

  if (loading) {
    return (
      <div className="catalog-page">
        <Topbar title={LOCATIONS_PAGE.title} subtitle={LOCATIONS_PAGE.subtitle} />
        <PageLoader />
      </div>
    )
  }

  return (
    <div className="catalog-page">
      <Topbar title={LOCATIONS_PAGE.title} subtitle={LOCATIONS_PAGE.subtitle} />
      {error ? (
        <section className="panel" style={{ padding: 16 }}>
          <p style={{ color: 'var(--danger)' }}>{error}</p>
        </section>
      ) : null}

      <section className="panel vendor-panel">
        <div className="panel__header">
          <h2 className="panel__title">{LOCATIONS_PAGE.listTitle}</h2>
          {location ? null : (
            <button type="button" className="btn btn--create" onClick={openCreate}>
              {LOCATIONS_PAGE.add}
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{LOCATIONS_PAGE.colName}</th>
                <th>{LOCATIONS_PAGE.colAddress}</th>
                <th>{LOCATIONS_PAGE.colCoords}</th>
                <th>{LOCATIONS_PAGE.colExpress}</th>
                <th>{LOCATIONS_PAGE.colStatus}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!location ? (
                <tr>
                  <td colSpan={6} className="table-empty">
                    {LOCATIONS_PAGE.empty}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td className="vendor-cell__name">{location.name}</td>
                  <td className="cell-muted">{displayAddress(location.address)}</td>
                  <td className="cell-muted">
                    {formatCoordinates(location.latitude, location.longitude)}
                  </td>
                  <td>
                    {location.supportsExpress ? (
                      <span className="status-badge status-badge--active">
                        {VENDOR_BADGES.express}
                      </span>
                    ) : (
                      <span className="status-badge status-badge--inactive">
                        {VENDOR_BADGES.standard}
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusToggle
                      checked={location.isActive}
                      label={statusToggleLabel(location.name)}
                      busy={busyId === location.id}
                      onChange={() => {
                        void toggleActive(location)
                      }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => openEdit(location)}
                    >
                      {LOCATIONS_PAGE.edit}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        title={editing ? LOCATIONS_PAGE.editTitle : LOCATIONS_PAGE.createTitle}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        closeDisabled={saving}
      >
        <form className="catalog-form" onSubmit={onSubmit} aria-busy={saving}>
          <div className="catalog-form__grid">
            <LocationSearchInput
              disabled={saving}
              onSelect={handleLocationSelect}
            />

            <label className="form-field form-field--wide">
              <span className="form-field__label">
                {LOCATIONS_PAGE.nameLabel} <em>*</em>
              </span>
              <input
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>

            <label className="form-field form-field--wide">
              <span className="form-field__label">{LOCATIONS_PAGE.addressLabel}</span>
              <textarea
                value={form.address}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    address: event.target.value,
                  }))
                }
              />
            </label>

            <label className="form-field">
              <span className="form-field__label">
                {LOCATIONS_PAGE.latitudeLabel} <em>*</em>
              </span>
              <input
                required
                inputMode="decimal"
                value={form.latitude}
                readOnly
                disabled={saving}
              />
            </label>

            <label className="form-field">
              <span className="form-field__label">
                {LOCATIONS_PAGE.longitudeLabel} <em>*</em>
              </span>
              <input
                required
                inputMode="decimal"
                value={form.longitude}
                readOnly
                disabled={saving}
              />
            </label>

            <label className="form-check">
              <input
                type="checkbox"
                checked={form.supportsExpress}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    supportsExpress: event.target.checked,
                  }))
                }
              />
              {LOCATIONS_PAGE.expressLabel}
            </label>

            <label className="form-check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
              />
              {LOCATIONS_PAGE.activeLabel}
            </label>
          </div>

          {formError ? (
            <p className="login-page__error" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="catalog-form__footer">
            <button
              type="button"
              className="btn btn--outline"
              disabled={saving}
              onClick={() => setModalOpen(false)}
            >
              {LOCATIONS_PAGE.cancel}
            </button>
            <button
              type="submit"
              className="btn btn--create"
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? (
                <Spinner inline label={LOADING_LABELS.saving} />
              ) : editing ? (
                LOCATIONS_PAGE.save
              ) : (
                LOCATIONS_PAGE.add
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
